import React, { useState } from 'react';
import axios, { AxiosError } from 'axios';
import {
    didApiCallFailDueToUnauthorizedUser,
    didApiCallFailDueToVersionMismatch,
    getErrorMessage,
} from '../../../utils/apiUtils';
import { useCommonMessageAndSpinnerHandlers } from '../../../hooks';
import { ErrorType } from '../../../types/commonTypes';

const useFetchOptions = <T extends {}>(api: string, searchString: string) => {
    const {
        errorMessage,
        setSessionTimeout,
        setAppVersionMismatch,
    } = useCommonMessageAndSpinnerHandlers();
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [options, setOptions] = useState<Array<T>>([]);
    const deferredSearchString = React.useDeferredValue(searchString);

    const fetchOptions = async (api_: string, searchString_: string) => {
        try {
            const response = await axios.get(`${api_}${encodeURIComponent(searchString_)}`);
            const responseData = response?.data;
            if (responseData) {
                setIsLoading(false);
                setHasError(false);
                setOptions(responseData);
            }
        } catch (error) {
            const axiosError = error as AxiosError<ErrorType>;
            setIsLoading(false);
            setHasError(true);
            setOptions([]);
            if (didApiCallFailDueToUnauthorizedUser(axiosError)) {
                setSessionTimeout({
                    timeout: true,
                });
            } else if (didApiCallFailDueToVersionMismatch(axiosError)) {
                setAppVersionMismatch(true);
            } else {
                errorMessage(getErrorMessage(axiosError));
            }
        }
    };

    React.useEffect(() => {
        if (api) {
            setIsLoading(true);
            fetchOptions(api, deferredSearchString);
        }
    }, [api, deferredSearchString]);

    return { isLoading, hasError, options };
};

export default useFetchOptions;
